import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Icon } from '@iconify/react';
import { Chip, Checkbox, Input, Textarea, Spinner, DatePicker } from '@nextui-org/react';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { I18nProvider } from '@react-aria/i18n';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast } from '../toast/CtyToast';
import {
  LabeledHorizontalRadios,
  getClaimRequests,
  isVehicleTripIntakeComplete,
  isTireProductType,
  TWE_SUPPORTING_DOCUMENT_CHIPS,
  clampTireTreadInput,
  makeEmptyLaborLineRow,
  makeEmptyPartLineRow,
  makeInitialClaimRepair,
  mapClaimRequestsToRepairEntries,
  mergeMappedRepairsWithUserEdits,
  type ClaimGenerationReviewData,
  type ClaimLaborLineRow,
  type ClaimPartLineRow,
  type ClaimRepairEntry,
  type ContractData,
  normalizeRepairOrderDateToMdy,
  repairOrderDateToComparableYmd,
  servicerPostalReady,
  servicerPhoneReady,
  buildServicerFetchKey,
  servicerLookupChanged,
} from './claimIntakeUtils';
import {
  isLaborLineNonEmpty,
  isPartLineNonEmpty,
  repairAllowsRemovingLaborLine,
  repairAllowsRemovingPartLine,
  repairHasAtLeastOneLineItem,
} from './buildClaimGenerationPayload';
import { claimObjectHasPartAndLaborLines, stableStringifyForScoreKey } from './claimScoreFormState';
import { runClaimIntakeOperation, type ClaimIntakeSession } from './claimIntakeAgentApi';
import { ClaimSubmitErrorModal, type ClaimSubmitErrorModalContent } from './ClaimSubmitErrorModal';
import { VehicleNotAtCenterModal } from './VehicleNotAtCenterModal';
import { CLAIM_INTAKE_GENERIC_ERROR_MESSAGE, buildGenericClaimIntakeErrorModal } from './claimSubmitErrorUtils';
import {
  buildUnifiedClaimPayload,
  isAmountOnlyLaborLine,
  recalcLaborLinesForRateChange,
  parseNumericField,
  extractServicerFromClaimBody,
  mergeServicerApiData,
  normalizeServicerClassification,
  formatAmountInputDisplay,
  sanitizeCurrencyAmountInput,
  type ClaimCustomerData,
  type ServicerApiData,
} from './buildUnifiedClaimPayload';
import {
  calcLineAmountString,
  handleLaborFieldChange,
  handleLaborLineFieldChange,
  resolveLaborLineAmountDisplay,
  resolvePartLineAmountDisplay,
} from './labor-calculation.utils';
import { mergeClaimObjectWithFormEdits, mergeClaimFormEditsPreferUi } from './mergeClaimObjectFormEdits';
import {
  buildClaimFormEditsFromParserResults,
  readParserResultsFromDocument,
} from './claimIntakeParser';
import {
  firstRepairsDocsErrorScrollId,
  isRepairsDocsInvoiceMetaComplete,
  isRepairsDocsServiceInfoComplete,
  repairsDocsFieldScrollId,
  sanitizePostalCodeInput,
  sanitizeDecimalInput,
  validateRepairsDocsTab,
  type LaborLineFieldErrors,
  type PartLineFieldErrors,
  type RepairsDocsTabFormValues,
} from './repairsDocsTabValidation';
import { VehicleNotesSection } from './VehicleNotesSection';
import { isPartsLaborWarrantyComplete, type VehicleNotesFormState } from './vehicleNotesUtils';
import type { AdditionalSourceItem } from './additionalInformationUpload';
import { resolveDocumentFileSources } from './additionalInformationUpload';
import { AdditionalInformationUploadSection } from './AdditionalInformationUploadSection';

const VEHICLE_AT_CENTER_INFO_DISCLAIMER =
  'The vehicle is required to remain at the service center until the claims process is complete.';

function isRepair3CsComplete(r: ClaimRepairEntry): boolean {
  return Boolean(r.complaint.trim() && r.cause.trim() && r.correction.trim());
}

function isRepairTireTreadComplete(r: ClaimRepairEntry): boolean {
  const trimmed = r.tiretread.trim();
  if (!trimmed) return false;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 && n <= 32;
}

/** True when estimateparser has populated at least one repair row or line item into the form. */
function hasEstimateParserPrefill(repairs: ClaimRepairEntry[]): boolean {
  return repairs.some(
    (r) =>
      Boolean(r.complaint.trim() || r.cause.trim() || r.correction.trim()) ||
      r.labor_lines.some(isLaborLineNonEmpty) ||
      r.part_lines.some(isPartLineNonEmpty)
  );
}

function resolveServicerInfoFormState(data: Record<string, unknown>): Record<string, unknown> {
  const nested = data.form_state;
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return data;
}

const SERVICER_CLASSIFICATION_OPTIONS = [
  { value: 'FRANCHISE', label: 'Franchise' },
  { value: 'INDEPENDENT', label: 'Independent' },
] as const;

function buildStreamUserInputExtras(
  paymentAuthorizationAcknowledged: boolean,
  servicerClassification: string
): Record<string, unknown> {
  const normalized = normalizeServicerClassification(servicerClassification);
  return {
    paymentAuthorizationAcknowledged,
    ...(normalized ? { servicer_classification: normalized } : {}),
  };
}

function inputCell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

function shouldShowPartNumberHint(row: ClaimPartLineRow): boolean {
  if (row.part_number.trim()) return false;
  return Boolean(
    row.part_description.trim() && row.part_quantity.trim() && row.part_unit_price.trim()
  );
}

function PartNumberLineCell({
  value,
  onValueChange,
  ariaLabel,
  isInvalid,
  errorMessage,
  showHint,
}: Readonly<{
  value: unknown;
  onValueChange: (v: string) => void;
  ariaLabel: string;
  isInvalid?: boolean;
  errorMessage?: string;
  showHint: boolean;
}>) {
  return (
    <td className="px-1.5 py-2 align-top">
      <Input
        size="sm"
        variant="bordered"
        aria-label={ariaLabel}
        value={inputCell(value)}
        onValueChange={onValueChange}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        classNames={{
          base: 'min-w-0 w-full',
          inputWrapper: 'h-8 min-h-[32px] border-default-200 bg-content1 px-2 shadow-none',
          input: 'text-sm py-0',
        }}
      />
      {showHint ? (
        <p className="text-tiny text-blue-500 dark:text-blue-400 mt-1 px-0.5">
          {'Providing a part number may help in quick evaluation of the claim.'}
        </p>
      ) : null}
    </td>
  );
}

function LaborAmountLineCell({
  value,
  onValueChange,
  ariaLabel,
  allowFixedValue,
  onAllowFixedValueChange,
  dense = false,
}: Readonly<{
  value: string;
  onValueChange: (v: string) => void;
  ariaLabel: string;
  allowFixedValue: boolean;
  onAllowFixedValueChange: (checked: boolean) => void;
  dense?: boolean;
}>) {
  const [isFocused, setIsFocused] = useState(false);
  const displayValue = isFocused ? value : formatAmountInputDisplay(value);

  return (
    <td className={dense ? 'px-1.5 py-2 align-top' : 'px-3 py-2 align-top'}>
      <Input
        size="sm"
        variant="bordered"
        aria-label={ariaLabel}
        value={displayValue}
        onValueChange={(v) => onValueChange(sanitizeCurrencyAmountInput(v))}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        inputMode="decimal"
        startContent={<span className="text-sm text-default-400">$</span>}
        classNames={{
          base: 'min-w-0 w-full',
          inputWrapper: 'h-8 min-h-[32px] border-default-200 bg-content1 px-2 shadow-none',
          input: 'text-sm py-0',
        }}
      />
      <Checkbox
        size="sm"
        className="mt-1.5"
        isSelected={allowFixedValue}
        onValueChange={onAllowFixedValueChange}
      >
        <span className="text-xs text-default-500">{'Allow fixed value'}</span>
      </Checkbox>
    </td>
  );
}

function CurrencyAmountLineCell({
  value,
  onValueChange,
  ariaLabel,
  isInvalid,
  errorMessage,
  dense = false,
}: Readonly<{
  value: unknown;
  onValueChange: (v: string) => void;
  ariaLabel: string;
  isInvalid?: boolean;
  errorMessage?: string;
  dense?: boolean;
}>) {
  const [isFocused, setIsFocused] = useState(false);
  const raw = inputCell(value);
  const displayValue = isFocused ? raw : formatAmountInputDisplay(raw);

  return (
    <td className={dense ? 'px-1.5 py-2 align-top' : 'px-3 py-2 align-top'}>
      <Input
        size="sm"
        variant="bordered"
        aria-label={ariaLabel}
        value={displayValue}
        onValueChange={(v) => onValueChange(sanitizeCurrencyAmountInput(v))}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        inputMode="decimal"
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        startContent={<span className="text-sm text-default-400">$</span>}
        classNames={{
          base: 'min-w-0 w-full',
          inputWrapper: 'h-8 min-h-[32px] border-default-200 bg-content1 px-2 shadow-none',
          input: 'text-sm py-0',
        }}
      />
    </td>
  );
}

type DetailFieldPath = 'description' | 'componentDesc' | 'partNumber' | 'requested.quantity' | 'requested.amount';

const LINE_TABLE_CLASS = 'w-full table-fixed border-collapse text-sm text-default-700';
const LINE_TH_DESC_CLASS = 'px-3 py-2';
const LINE_TH_DATA_CLASS = 'px-1.5 py-2 whitespace-nowrap';
const LINE_TH_ACTION_CLASS = 'w-10 px-1 py-2';

type LineTableLayout = 'failureLabor' | 'failurePart' | 'pregenLabor' | 'pregenPart';

/** Percent widths sum to 100% so non-description columns fill the row with no gaps. */
const LINE_TABLE_COL_WIDTHS: Record<LineTableLayout, readonly string[]> = {
  failureLabor: ['24%', '38%', '38%'],
  failurePart: ['22%', '22%', '28%', '28%'],
  pregenLabor: ['36%', '22%', '22%', '22%', '4%'],
  pregenPart: ['30%', '22%', '14%', '14%', '10%', '4%'],
};

function LineTableColGroup({ layout }: Readonly<{ layout: LineTableLayout }>) {
  return (
    <colgroup>
      {LINE_TABLE_COL_WIDTHS[layout].map((width, i) => (
        <col key={`${layout}-${i}`} style={{ width }} />
      ))}
    </colgroup>
  );
}



type DetailEntry = Readonly<{ row: Record<string, unknown>; originalIdx: number }>;

type ProceedFooterHintContext = {
  serviceInfoComplete: boolean;
  vehicleTripComplete: boolean;
  paymentAuthorizationAcknowledged: boolean;
  invoiceMetaComplete: boolean;
  draftLinesComplete: boolean;
  repairsThreeCsComplete: boolean;
  hasGeneratedLineItems: boolean;
  showFieldErrors: boolean;
  partsLaborWarrantyComplete: boolean;
  token: string | null;
};

function getProceedFooterHintMessage(ctx: ProceedFooterHintContext): string | undefined {
  if (!ctx.serviceInfoComplete) return 'Complete service center information.';
  if (!ctx.vehicleTripComplete)
    return 'Select how the vehicle arrived, whether it is at the service center, and reimbursement.';
  if (!ctx.paymentAuthorizationAcknowledged)
    return 'Acknowledge the repair authorization disclaimer in the payment section.';
  if (!ctx.invoiceMetaComplete)
    return 'Complete invoice fields (mileage, invoice #, repair date), contract number, and customer details.';
  if (!ctx.repairsThreeCsComplete)
    return 'Complete Complaint, Cause, and Correction for every repair.';
  if (!ctx.draftLinesComplete)
    return 'Each repair needs at least one labor or part line before continuing.';
  if (ctx.showFieldErrors && !ctx.partsLaborWarrantyComplete) {
    return 'Complete the required fields to continue.';
  }
  if (!ctx.token) return 'Sign in to submit this claim.';
  return undefined;
}

function EditableLineCell({
  value,
  onValueChange,
  onFocus,
  onBlur,
  inputMode,
  ariaLabel,
  className,
  isInvalid,
  errorMessage,
  maxLength,
  dense = false,
}: Readonly<{
  value: unknown;
  onValueChange: (v: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  inputMode?: 'text' | 'decimal';
  ariaLabel: string;
  className?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  maxLength?: number;
  /** Tighter cell padding for numeric / part columns. */
  dense?: boolean;
}>) {
  return (
    <td className={dense ? 'px-1.5 py-2 align-top' : 'px-3 py-2 align-top'}>
    <Input
      size="sm"
      variant="bordered"
      aria-label={ariaLabel}
      value={inputCell(value)}
      onValueChange={onValueChange}
      onFocus={onFocus}
      onBlur={onBlur}
      inputMode={inputMode}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      maxLength={maxLength}
      classNames={{
        base: `min-w-0 w-full ${className ?? ''}`,
        inputWrapper: 'h-8 min-h-[32px] border-default-200 bg-content1 px-2 shadow-none',
        input: 'text-sm py-0',
      }}
    />
    </td>
  );
}

type LineItemTablesProps = Readonly<{
  failureIdx: number;
  laborEntries: DetailEntry[];
  partEntries: DetailEntry[];
  onUpdateField: (failureIdx: number, detailIdx: number, field: DetailFieldPath, value: string) => void;
  onAddLaborLine: (failureIdx: number) => void;
  onAddPartLine: (failureIdx: number) => void;
  laborHeader: string;
  partHeader: string;
  noLabor: string;
  noPart: string;
  addLabor: string;
  addPart: string;
}>;

function LineItemTables({
  failureIdx,
  laborEntries,
  partEntries,
  onUpdateField,
  onAddLaborLine,
  onAddPartLine,
  laborHeader,
  partHeader,
  noLabor,
  noPart,
  addLabor,
  addPart,
}: LineItemTablesProps) {
  return (
    <div className="space-y-4">
      <div className="bg-default-100 border border-default-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-default-200 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-default-500">{laborHeader}</span>
          <CtyButton variant="light" size="sm" onPress={() => onAddLaborLine(failureIdx)} className="font-semibold">
            {addLabor}
          </CtyButton>
        </div>
        <div className="overflow-x-auto">
          <table className={LINE_TABLE_CLASS}>
            <LineTableColGroup layout="failureLabor" />
            <thead>
              <tr className="border-b border-default-200 bg-content2/60 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
                <th className={LINE_TH_DESC_CLASS}>Description</th>
                <th className={LINE_TH_DATA_CLASS}>Hours (hrs)</th>
                <th className={LINE_TH_DATA_CLASS}>Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              {laborEntries.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-3 text-center text-default-400">
                    {noLabor}
                  </td>
                </tr>
              ) : (
                laborEntries.map(({ row, originalIdx }) => {
                  const requested =
                    row.requested && typeof row.requested === 'object' ? (row.requested as Record<string, unknown>) : {};
                  return (
                    <tr key={`labor-${failureIdx}-${originalIdx}`} className="border-b border-default-200 last:border-0">
                        <EditableLineCell
                          value={row.description}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'description', v)}
                          ariaLabel="Description"
                        />
                        <EditableLineCell
                          value={requested.quantity}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'requested.quantity', sanitizeDecimalInput(v))}
                          inputMode="decimal"
                          ariaLabel="Hours"
                          dense
                        />
                        <CurrencyAmountLineCell
                          value={requested.amount}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'requested.amount', v)}
                          ariaLabel="Amount"
                          dense
                        />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-default-100 border border-default-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-default-200 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-default-500">{partHeader}</span>
          <CtyButton variant="light" size="sm" onPress={() => onAddPartLine(failureIdx)} className="font-semibold">
            {addPart}
          </CtyButton>
        </div>
        <div className="overflow-x-auto">
          <table className={LINE_TABLE_CLASS}>
            <LineTableColGroup layout="failurePart" />
            <thead>
              <tr className="border-b border-default-200 bg-content2/60 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
                <th className={LINE_TH_DESC_CLASS}>Description</th>
                <th className={LINE_TH_DATA_CLASS}>Part #</th>
                <th className={LINE_TH_DATA_CLASS}>Qty</th>
                <th className={LINE_TH_DATA_CLASS}>Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              {partEntries.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-center text-default-400">
                    {noPart}
                  </td>
                </tr>
              ) : (
                partEntries.map(({ row, originalIdx }) => {
                  const requested =
                    row.requested && typeof row.requested === 'object' ? (row.requested as Record<string, unknown>) : {};
                  return (
                    <tr key={`part-${failureIdx}-${originalIdx}`} className="border-b border-default-200 last:border-0">
                        <EditableLineCell
                          value={row.description}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'description', v)}
                          ariaLabel="Description"
                        />
                        <EditableLineCell
                          value={row.partNumber}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'partNumber', v)}
                          ariaLabel="Part #"
                          dense
                        />
                        <EditableLineCell
                          value={requested.quantity}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'requested.quantity', sanitizeDecimalInput(v))}
                          inputMode="decimal"
                          ariaLabel="Qty"
                          dense
                        />
                        <CurrencyAmountLineCell
                          value={requested.amount}
                          onValueChange={(v) => onUpdateField(failureIdx, originalIdx, 'requested.amount', v)}
                          ariaLabel="Amount"
                          dense
                        />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

type PreGenLineTablesProps = Readonly<{
  repairIdx: number;
  laborRate: string;
  laborLines: ClaimLaborLineRow[];
  partLines: ClaimPartLineRow[];
  onLaborField: (repairIdx: number, lineIdx: number, field: keyof ClaimLaborLineRow, value: string) => void;
  onLaborHoursFocus: (repairIdx: number, lineIdx: number) => void;
  onLaborHoursBlur: (repairIdx: number, lineIdx: number) => void;
  isAmountOnlyPendingLine: (lineId: number) => boolean;
  onPartField: (repairIdx: number, lineIdx: number, field: keyof ClaimPartLineRow, value: string) => void;
  onAddLabor: (repairIdx: number) => void;
  onAddPart: (repairIdx: number) => void;
  onRemoveLabor: (repairIdx: number, lineIdx: number) => void;
  onRemovePart: (repairIdx: number, lineIdx: number) => void;
  canRemoveLabor: (lineIdx: number) => boolean;
  canRemovePart: (lineIdx: number) => boolean;
  laborHeader: string;
  partHeader: string;
  descLabel: string;
  hoursLabel: string;
  amountLabel: string;
  opCodeLabel: string;
  partNumLabel: string;
  qtyLabel: string;
  unitPriceLabel: string;
  addLabor: string;
  addPart: string;
  showFieldErrors: boolean;
  lineItemsSummary?: string;
  laborLineErrors?: Partial<Record<number, LaborLineFieldErrors>>;
  partLineErrors?: Partial<Record<number, PartLineFieldErrors>>;
}>;

function PreGenLineTables({
  repairIdx,
  laborRate,
  laborLines,
  partLines,
  onLaborField,
  onLaborHoursFocus,
  onLaborHoursBlur,
  isAmountOnlyPendingLine,
  onPartField,
  onAddLabor,
  onAddPart,
  onRemoveLabor,
  onRemovePart,
  canRemoveLabor,
  canRemovePart,
  laborHeader,
  partHeader,
  descLabel,
  hoursLabel,
  amountLabel,
  opCodeLabel,
  partNumLabel,
  qtyLabel,
  unitPriceLabel,
  addLabor,
  addPart,
  showFieldErrors,
  lineItemsSummary,
  laborLineErrors,
  partLineErrors,
}: PreGenLineTablesProps) {
  const summaryError = showFieldErrors ? lineItemsSummary : undefined;
  return (
    <div className="space-y-4">
      {summaryError ? <p className="text-tiny text-danger px-1">{summaryError}</p> : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="px-1 text-xs font-semibold uppercase tracking-wide text-default-500">{laborHeader}</div>
        <CtyButton variant="bordered" color='primary' size="sm" onPress={() => onAddLabor(repairIdx)} className="font-semibold shrink-0">
          {addLabor}
        </CtyButton>
      </div>
      <div className="bg-default-100 border border-default-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className={LINE_TABLE_CLASS}>
            <LineTableColGroup layout="pregenLabor" />
            <thead>
              <tr className="border-b border-default-200 bg-content2/60 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
                <th className={LINE_TH_DESC_CLASS}>{descLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{opCodeLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{hoursLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{amountLabel}</th>
                <th className={LINE_TH_ACTION_CLASS} />
              </tr>
            </thead>
            <tbody>
              {laborLines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-center text-default-400 text-sm">
                    {"No Labor Lines. Add one below or use Part Lines only."}
                  </td>
                </tr>
              ) : null}
              {laborLines.map((row, lineIdx) => {
                const laborErr = showFieldErrors ? laborLineErrors?.[lineIdx] : undefined;
                return (
                <tr key={row.id} className="border-b border-default-200 last:border-0">
                  <EditableLineCell
                    value={row.labor_description}
                    onValueChange={(v) => onLaborField(repairIdx, lineIdx, 'labor_description', v)}
                    ariaLabel={descLabel}
                    isInvalid={Boolean(laborErr?.labor_description)}
                    errorMessage={laborErr?.labor_description}
                    maxLength={250}
                  />
                  <EditableLineCell
                    value={row.labor_op_code}
                    onValueChange={(v) => onLaborField(repairIdx, lineIdx, 'labor_op_code', v)}
                    ariaLabel={opCodeLabel}
                    dense
                  />
                  <EditableLineCell
                    value={row.labor_hours}
                    onValueChange={(v) => onLaborField(repairIdx, lineIdx, 'labor_hours', v)}
                    onFocus={
                      isAmountOnlyPendingLine(row.id)
                        ? () => onLaborHoursFocus(repairIdx, lineIdx)
                        : undefined
                    }
                    onBlur={
                      isAmountOnlyPendingLine(row.id)
                        ? () => onLaborHoursBlur(repairIdx, lineIdx)
                        : undefined
                    }
                    inputMode="decimal"
                    ariaLabel={hoursLabel}
                    isInvalid={Boolean(laborErr?.labor_hours)}
                    errorMessage={laborErr?.labor_hours}
                    dense
                  />
                  <LaborAmountLineCell
                    value={resolveLaborLineAmountDisplay(row, laborRate)}
                    onValueChange={(v) => onLaborField(repairIdx, lineIdx, 'labor_amount', v)}
                    ariaLabel={amountLabel}
                    allowFixedValue={row.allowFixedValue}
                    onAllowFixedValueChange={(checked) =>
                      onLaborField(repairIdx, lineIdx, 'allowFixedValue', String(checked))
                    }
                    dense
                  />
                  <td className="px-1 py-1 align-middle">
                    {canRemoveLabor(lineIdx) ? (
                      <button
                        type="button"
                        className="text-default-400 hover:text-red-500 p-1"
                        onClick={() => onRemoveLabor(repairIdx, lineIdx)}
                        aria-label="Remove labor line"
                      >
                        <Icon icon="mdi:close" width={16} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="px-1 text-xs font-semibold uppercase tracking-wide text-default-500">{partHeader}</div>
        <CtyButton variant="bordered" color="primary" size="sm" onPress={() => onAddPart(repairIdx)} className="font-semibold shrink-0">
          {addPart}
        </CtyButton>
      </div>
      <div className="bg-default-100 border border-default-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className={LINE_TABLE_CLASS}>
            <LineTableColGroup layout="pregenPart" />
            <thead>
              <tr className="border-b border-default-200 bg-content2/60 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
                <th className={LINE_TH_DESC_CLASS}>{descLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{partNumLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{qtyLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{unitPriceLabel}</th>
                <th className={LINE_TH_DATA_CLASS}>{amountLabel}</th>
                <th className={LINE_TH_ACTION_CLASS} />
              </tr>
            </thead>
            <tbody>
              {partLines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-3 text-center text-default-400 text-sm">
                    {"No Part Lines. Add one below or use Labor Lines only."}
                  </td>
                </tr>
              ) : null}
              {partLines.map((row, lineIdx) => {
                const partErr = showFieldErrors ? partLineErrors?.[lineIdx] : undefined;
                return (
                <tr key={row.id} className="border-b border-default-200 last:border-0">
                    <EditableLineCell
                      value={row.part_description}
                      onValueChange={(v) => onPartField(repairIdx, lineIdx, 'part_description', v)}
                      ariaLabel={descLabel}
                      isInvalid={Boolean(partErr?.part_description)}
                      errorMessage={partErr?.part_description}
                      maxLength={250}
                    />
                    <PartNumberLineCell
                      value={row.part_number}
                      onValueChange={(v) => onPartField(repairIdx, lineIdx, 'part_number', v)}
                      ariaLabel={partNumLabel}
                      isInvalid={Boolean(partErr?.part_number)}
                      errorMessage={partErr?.part_number}
                      showHint={shouldShowPartNumberHint(row)}
                    />
                    <EditableLineCell
                      value={row.part_quantity}
                      onValueChange={(v) => onPartField(repairIdx, lineIdx, 'part_quantity', v)}
                      inputMode="decimal"
                      ariaLabel={qtyLabel}
                      isInvalid={Boolean(partErr?.part_quantity)}
                      errorMessage={partErr?.part_quantity}
                      dense
                    />
                    <CurrencyAmountLineCell
                      value={row.part_unit_price}
                      onValueChange={(v) => onPartField(repairIdx, lineIdx, 'part_unit_price', v)}
                      ariaLabel={unitPriceLabel}
                      isInvalid={Boolean(partErr?.part_unit_price)}
                      errorMessage={partErr?.part_unit_price}
                      dense
                    />
                    <CurrencyAmountLineCell
                      value={resolvePartLineAmountDisplay(row)}
                      onValueChange={(v) => onPartField(repairIdx, lineIdx, 'part_amount', v)}
                      ariaLabel={amountLabel}
                      dense
                    />
                  <td className="px-1 py-1 align-middle">
                    {canRemovePart(lineIdx) ? (
                      <button
                        type="button"
                        className="text-default-400 hover:text-red-500 p-1"
                        onClick={() => onRemovePart(repairIdx, lineIdx)}
                        aria-label="Remove part line"
                      >
                        <Icon icon="mdi:close" width={16} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function buildClaimFormEditsFromProps(params: {
  repairs: ClaimRepairEntry[];
  laborRate: string;
  mileageIn: string;
  invoiceNumber: string;
  repairOrderDate: string;
  arrivalMethod: string;
  atServiceCenter: string;
  reimbursement: string;
  paymentEmail: string;
  vehicleNotes: VehicleNotesFormState;
  additionalSources?: AdditionalSourceItem[];
  sources?: AdditionalSourceItem[];
  customerName: string;
  customerPhone: string;
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerPostalCode: string;
  customerCountryCode: string;
  servicerName: string;
  servicerPhone: string;
  servicerContactName: string;
  servicerMobile: string;
  wantText: string;
  contactEmail: string;
  servicerAddress1: string;
  servicerAddress2: string;
  servicerCity: string;
  servicerState: string;
  servicerPostalCode: string;
  servicerCountryCode: string;
  dealerNumber: string;
}) {
  return {
    repairs: params.repairs,
    laborRate: params.laborRate,
    mileageIn: params.mileageIn,
    invoiceNumber: params.invoiceNumber,
    repairOrderDate: params.repairOrderDate,
    arrivalMethod: params.arrivalMethod,
    atServiceCenter: params.atServiceCenter,
    reimbursement: params.reimbursement,
    paymentEmail: params.paymentEmail,
    vehicleNotes: params.vehicleNotes,
    additionalSources: params.additionalSources,
    sources: params.sources,
    customerName: params.customerName,
    customerPhone: params.customerPhone,
    customerAddress: {
      address1: params.customerAddress1,
      address2: params.customerAddress2,
      city: params.customerCity,
      state: params.customerState,
      postalCode: params.customerPostalCode,
      countryCode: params.customerCountryCode,
    },
    servicerName: params.servicerName,
    servicerPhone: params.servicerPhone,
    servicerContactName: params.servicerContactName,
    servicerMobile: params.servicerMobile,
    wantText: params.wantText,
    contactEmail: params.contactEmail,
    servicerAddress: {
      address1: params.servicerAddress1,
      address2: params.servicerAddress2,
      city: params.servicerCity,
      state: params.servicerState,
      postalCode: params.servicerPostalCode,
      countryCode: params.servicerCountryCode,
    },
    dealerNumber: params.dealerNumber,
  };
}

export function RepairsDocsTab({
  paymentAuthorizationAcknowledged,
  setPaymentAuthorizationAcknowledged,
  session,
  agentsBaseUrl,
  aiworkerUsageId,
  contract,
  contractNumber,
  dealerNumber,
  onDealerNumberChange: _onDealerNumberChange,
  productType,
  parsedClaimDocument,
  restoredClaimGeneration,
  preparedClaimRequestKey,
  onPreparedClaimRequestKeyChange,
  arrivalMethod,
  onArrivalMethodChange,
  atServiceCenter,
  onAtServiceCenterChange,
  reimbursement,
  onReimbursementChange,
  paymentEmail,
  onPaymentEmailChange,
  vehicleNotes,
  onVehicleNotesChange,
  additionalInfoFiles = [],
  onAdditionalInfoFilesChange,
  additionalSources,
  onAdditionalSourcesChange,
  documentFileSources,
  servicerName,
  onServicerNameChange,
  servicerPhone,
  onServicerPhoneChange,
  servicerContactName,
  onServicerContactNameChange,
  servicerMobile,
  onServicerMobileChange,
  wantText,
  onWantTextChange,
  servicerAddress1,
  onServicerAddress1Change,
  servicerAddress2,
  onServicerAddress2Change,
  servicerCity,
  onServicerCityChange,
  servicerState,
  onServicerStateChange,
  servicerPostalCode,
  onServicerPostalCodeChange,
  servicerCountryCode,
  onServicerCountryCodeChange,
  servicerClassification,
  onServicerClassificationChange,
  contactEmail,
  onContactEmailChange,
  laborRate,
  onLaborRateChange,
  mileageIn,
  onMileageInChange,
  invoiceNumber,
  onInvoiceNumberChange,
  repairOrderDate,
  onRepairOrderDateChange,
  vehicleVin,
  customerName,
  onCustomerNameChange,
  customerPhone,
  onCustomerPhoneChange,
  customerAddress1,
  onCustomerAddress1Change,
  customerAddress2,
  onCustomerAddress2Change,
  customerCity,
  onCustomerCityChange,
  customerState,
  onCustomerStateChange,
  customerPostalCode,
  onCustomerPostalCodeChange,
  customerCountryCode,
  onCustomerCountryCodeChange,
  repairs,
  onRepairsChange,
  token,
  onBack,
  onContinue,
  onClaimScoreEnrichedForm,
  onClaimGenerationReviewChange,
  onCustomerFromApiChange,
  onServicerFromApiChange,
  servicerSourceId,
  dealerSourceId,
}: Readonly<{
  paymentAuthorizationAcknowledged: boolean;
  setPaymentAuthorizationAcknowledged: (v: boolean) => void;
  session: ClaimIntakeSession | null;
  agentsBaseUrl?: string;
  /** The session's aiworker_usage_id — passed to API calls for unified backend logging. */
  aiworkerUsageId?: string;
  contract: ContractData | null;
  contractNumber: string;
  dealerNumber: string;
  onDealerNumberChange: (v: string) => void;
  productType: string;
  parsedClaimDocument: Record<string, unknown> | null;
  restoredClaimGeneration?: ClaimGenerationReviewData | null;
  /** Serialized `requests` array that produced the current prepared claim. */
  preparedClaimRequestKey?: string | null;
  onPreparedClaimRequestKeyChange?: (key: string | null) => void;
  arrivalMethod: string;
  onArrivalMethodChange: (v: string) => void;
  atServiceCenter: string;
  onAtServiceCenterChange: (v: string) => void;
  reimbursement: string;
  onReimbursementChange: (v: string) => void;
  paymentEmail: string;
  onPaymentEmailChange: (v: string) => void;
  vehicleNotes: VehicleNotesFormState;
  onVehicleNotesChange: (patch: Partial<VehicleNotesFormState>) => void;
  additionalInfoFiles?: File[];
  onAdditionalInfoFilesChange?: Dispatch<SetStateAction<File[]>>;
  /** Supporting files from Additional Information — root `additional_sources` on the claim payload. */
  additionalSources?: AdditionalSourceItem[];
  onAdditionalSourcesChange?: Dispatch<SetStateAction<AdditionalSourceItem[]>>;
  /** Primary document upload sources — root `sources` on the claim payload. */
  documentFileSources?: AdditionalSourceItem[];
  servicerName: string;
  onServicerNameChange: (v: string) => void;
  /** Servicer phone — populated from 3csmapper response, user-editable. */
  servicerPhone: string;
  onServicerPhoneChange: (v: string) => void;
  servicerContactName: string;
  onServicerContactNameChange: (v: string) => void;
  servicerMobile: string;
  onServicerMobileChange: (v: string) => void;
  wantText: string;
  onWantTextChange: (v: string) => void;
  /** Servicer address fields — populated from 3csmapper response, user-editable. */
  servicerAddress1: string;
  onServicerAddress1Change: (v: string) => void;
  servicerAddress2: string;
  onServicerAddress2Change: (v: string) => void;
  servicerCity: string;
  onServicerCityChange: (v: string) => void;
  servicerState: string;
  onServicerStateChange: (v: string) => void;
  servicerPostalCode: string;
  onServicerPostalCodeChange: (v: string) => void;
  servicerCountryCode: string;
  onServicerCountryCodeChange: (v: string) => void;
  /** Servicer type label from servicer_info / 3csmapper (`servicer_classification`). */
  servicerClassification: string;
  onServicerClassificationChange: (v: string) => void;
  contactEmail: string;
  onContactEmailChange: (v: string) => void;
  laborRate: string;
  onLaborRateChange: (v: string) => void;
  mileageIn: string;
  onMileageInChange: (v: string) => void;
  invoiceNumber: string;
  onInvoiceNumberChange: (v: string) => void;
  repairOrderDate: string;
  onRepairOrderDateChange: (v: string) => void;
  vehicleVin: string;
  customerName: string;
  onCustomerNameChange: (v: string) => void;
  customerPhone: string;
  onCustomerPhoneChange: (v: string) => void;
  /** Customer address fields — pre-filled from contract, user-editable. */
  customerAddress1: string;
  onCustomerAddress1Change: (v: string) => void;
  customerAddress2: string;
  onCustomerAddress2Change: (v: string) => void;
  customerCity: string;
  onCustomerCityChange: (v: string) => void;
  customerState: string;
  onCustomerStateChange: (v: string) => void;
  customerPostalCode: string;
  onCustomerPostalCodeChange: (v: string) => void;
  customerCountryCode: string;
  onCustomerCountryCodeChange: (v: string) => void;
  repairs: ClaimRepairEntry[];
  onRepairsChange: Dispatch<SetStateAction<ClaimRepairEntry[]>>;
  token: string | null;
  onBack: () => void;
  onContinue: (data: ClaimGenerationReviewData) => void;
  onClaimScoreEnrichedForm?: (formState: Record<string, unknown> | null) => void;
  /** Called immediately whenever the generated/edited claim data changes so the parent can persist
   *  it across tab navigation without waiting for the user to press "Proceed to Submit". */
  onClaimGenerationReviewChange?: (data: ClaimGenerationReviewData | null) => void;
  /** Called with the customer data returned by 3csmapper so the parent can pass it to ReviewTab. */
  onCustomerFromApiChange?: (customer: ClaimCustomerData | null) => void;
  /** Called with the full servicer details returned by 3csmapper so the parent can update those fields. */
  onServicerFromApiChange?: (data: ServicerApiData) => void;
  /** Action source id for `Get Servicer Details` — used by servicer_info auto-fetch. */
  servicerSourceId?: string;
  /** Action source id for `Get Dealer Details` — used by servicer_info auto-fetch. */
  dealerSourceId?: string;
}>) {
  const [claimGenerationReview, setClaimGenerationReview] = useState<ClaimGenerationReviewData | null>(
    () => restoredClaimGeneration ?? null
  );
  const [shouldOpenReviewAfterPrepare, setShouldOpenReviewAfterPrepare] = useState(false);
  const [isGeneratingClaim, setIsGeneratingClaim] = useState(false);
  const [isFetchingServicerInfo, setIsFetchingServicerInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [isLaborRateFocused, setIsLaborRateFocused] = useState(false);
  const [submitErrorModal, setSubmitErrorModal] = useState<ClaimSubmitErrorModalContent | null>(null);
  const [vehicleNotAtCenterModalOpen, setVehicleNotAtCenterModalOpen] = useState(false);

  const handleAtServiceCenterChange = useCallback(
    (value: string) => {
      onAtServiceCenterChange(value);
      if (value === 'no') {
        setVehicleNotAtCenterModalOpen(true);
        return;
      }
      setVehicleNotAtCenterModalOpen(false);
    },
    [onAtServiceCenterChange]
  );

  /** Prefilled labor amount without hours — amount is held fixed while user enters rate or hours until blur. */
  const amountOnlyLaborLinesRef = useRef(new Map<number, string>());
  type LaborDerivationFocus = 'laborRate' | { repairIdx: number; lineIdx: number };
  const laborDerivationFocusRef = useRef<LaborDerivationFocus | null>(null);

  useEffect(() => {
    repairs.forEach((repair) => {
      repair.labor_lines.forEach((line) => {
        if (isAmountOnlyLaborLine(line) && !amountOnlyLaborLinesRef.current.has(line.id)) {
          amountOnlyLaborLinesRef.current.set(line.id, line.labor_amount.trim());
        }
      });
    });
  }, [repairs]);

  const isAmountOnlyPendingLine = useCallback((lineId: number) => {
    return amountOnlyLaborLinesRef.current.has(lineId);
  }, [repairs]);

  const handleLaborRateFocus = useCallback(() => {
    if (amountOnlyLaborLinesRef.current.size > 0) {
      laborDerivationFocusRef.current = 'laborRate';
    }
  }, []);

  const handleLaborRateBlur = useCallback(() => {
    if (laborDerivationFocusRef.current === 'laborRate') {
      laborDerivationFocusRef.current = null;
      amountOnlyLaborLinesRef.current.clear();
    }
  }, []);

  const handleLaborHoursFocus = useCallback(
    (repairIdx: number, lineIdx: number) => {
      const line = repairs[repairIdx]?.labor_lines[lineIdx];
      if (!line || !amountOnlyLaborLinesRef.current.has(line.id)) return;
      laborDerivationFocusRef.current = { repairIdx, lineIdx };
    },
    [repairs]
  );

  const handleLaborHoursBlur = useCallback(
    (repairIdx: number, lineIdx: number) => {
      const focus = laborDerivationFocusRef.current;
      if (
        focus &&
        typeof focus === 'object' &&
        focus.repairIdx === repairIdx &&
        focus.lineIdx === lineIdx
      ) {
        const line = repairs[repairIdx]?.labor_lines[lineIdx];
        if (line) amountOnlyLaborLinesRef.current.delete(line.id);
        laborDerivationFocusRef.current = null;
      }
    },
    [repairs]
  );

  const applyAmountOnlyLaborRateChange = useCallback(
    (nextRate: string) => {
      const rate = nextRate.trim();
      if (!rate || parseNumericField(rate) <= 0) return;

      const fixedAmountByLineId = amountOnlyLaborLinesRef.current;
      if (fixedAmountByLineId.size === 0) return;

      onRepairsChange((prev) => {
        let anyChanged = false;
        const next = prev.map((repair) => {
          const labor_lines = recalcLaborLinesForRateChange(repair.labor_lines, rate, {
            fixedAmountByLineId,
          });
          const changed = labor_lines.some(
            (line, i) =>
              line.labor_hours !== repair.labor_lines[i]?.labor_hours ||
              line.labor_amount !== repair.labor_lines[i]?.labor_amount
          );
          if (!changed) return repair;
          anyChanged = true;
          return { ...repair, labor_lines };
        });
        return anyChanged ? next : prev;
      });
    },
    [onRepairsChange]
  );

  const applyNormalLaborRateChange = useCallback(
    (nextRate: string) => {
      const rate = nextRate.trim();
      if (!rate || parseNumericField(rate) <= 0) return;

      onRepairsChange((prev) => {
        let anyChanged = false;
        const next = prev.map((repair) => {
          const labor_lines = repair.labor_lines.map((line) => {
            if (line.allowFixedValue) return line;
            if (amountOnlyLaborLinesRef.current.has(line.id)) return line;
            const hoursStr = line.labor_hours.trim();
            const hoursNum = parseNumericField(hoursStr);
            if (!hoursStr || hoursNum <= 0) return line;
            const computedAmount = calcLineAmountString(hoursStr, rate);
            if (!computedAmount || computedAmount === line.labor_amount) return line;
            anyChanged = true;
            return { ...line, labor_amount: computedAmount };
          });
          return { ...repair, labor_lines };
        });
        return anyChanged ? next : prev;
      });
    },
    [onRepairsChange]
  );

  const handleLaborRateChange = useCallback(
    (value: string) => {
      const nextRate = sanitizeDecimalInput(value);
      onLaborRateChange(nextRate);

      if (laborDerivationFocusRef.current === 'laborRate') {
        applyAmountOnlyLaborRateChange(nextRate);
      } else {
        applyNormalLaborRateChange(nextRate);
      }
    },
    [onLaborRateChange, applyAmountOnlyLaborRateChange, applyNormalLaborRateChange]
  );

  /**
   * Tracks the last value we received FROM the parent so we can distinguish "parent pushed a
   * new value" (don't echo back) from "child changed it locally" (propagate up to parent).
   * Initialized to the same lazy value as `claimGenerationReview` so the mount-time effect
   * does not fire an unnecessary callback.
   */
  const lastFromParentRef = useRef<ClaimGenerationReviewData | null>(restoredClaimGeneration ?? null);

  useEffect(() => {
    if (restoredClaimGeneration === lastFromParentRef.current) return;
    lastFromParentRef.current = restoredClaimGeneration ?? null;
    setClaimGenerationReview(restoredClaimGeneration ?? null);
  }, [restoredClaimGeneration]);

  // Propagate local changes (generation result, inline edits, added lines) up to the parent
  // immediately so the data survives tab navigation even before "Proceed to Submit" is pressed.
  useEffect(() => {
    if (claimGenerationReview === lastFromParentRef.current) return;
    onClaimGenerationReviewChange?.(claimGenerationReview);
  }, [claimGenerationReview, onClaimGenerationReviewChange]);
  useEffect(() => {
    if (!isGeneratingClaim) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isGeneratingClaim]);

  const resolvedFileSources = useMemo(
    () => resolveDocumentFileSources(documentFileSources, parsedClaimDocument),
    [documentFileSources, parsedClaimDocument]
  );

  const claimFormEdits = useMemo(
    () =>
      buildClaimFormEditsFromProps({
        repairs,
        laborRate,
        mileageIn,
        invoiceNumber,
        repairOrderDate,
        arrivalMethod,
        atServiceCenter,
        reimbursement,
        paymentEmail,
        vehicleNotes,
        additionalSources,
        sources: resolvedFileSources,
        customerName,
        customerPhone,
        customerAddress1,
        customerAddress2,
        customerCity,
        customerState,
        customerPostalCode,
        customerCountryCode,
        servicerName,
        servicerPhone,
        servicerContactName,
        servicerMobile,
        wantText,
        contactEmail,
        servicerAddress1,
        servicerAddress2,
        servicerCity,
        servicerState,
        servicerPostalCode,
        servicerCountryCode,
        dealerNumber,
      }),
    [
      repairs,
      laborRate,
      mileageIn,
      invoiceNumber,
      repairOrderDate,
      arrivalMethod,
      atServiceCenter,
      reimbursement,
      paymentEmail,
      vehicleNotes,
      additionalSources,
      resolvedFileSources,
      customerName,
      customerPhone,
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerPostalCode,
      customerCountryCode,
      servicerName,
      servicerPhone,
      servicerContactName,
      servicerMobile,
      wantText,
      contactEmail,
      servicerAddress1,
      servicerAddress2,
      servicerCity,
      servicerState,
      servicerPostalCode,
      servicerCountryCode,
      dealerNumber,
    ]
  );

  const buildApiFormState = useCallback((): Record<string, unknown> => {
    if (servicerInfoFormStateRef.current) {
      return mergeClaimObjectWithFormEdits(servicerInfoFormStateRef.current, claimFormEdits);
    }

    if (!contract) return {};

    return buildUnifiedClaimPayload({
      contract,
      customerName,
      customerPhone,
      customerAddress: {
        address1: customerAddress1,
        address2: customerAddress2,
        city: customerCity,
        state: customerState,
        postalCode: customerPostalCode,
        countryCode: customerCountryCode,
      },
      repairs,
      laborRate,
      servicerName,
      servicerPhone,
      servicerContactName,
      servicerMobile,
      wantText,
      contactEmail,
      paymentEmail,
      servicerAddress: {
        address1: servicerAddress1,
        address2: servicerAddress2,
        city: servicerCity,
        state: servicerState,
        postalCode: servicerPostalCode,
        countryCode: servicerCountryCode,
      },
      servicerDetailsNull: true,
      mileageIn,
      invoiceNumber,
      repairOrderDate,
      arrivalMethod,
      atServiceCenter,
      reimbursement,
      vehicleNotes,
      additionalSources,
      sources: resolvedFileSources,
      productType,
    }) as Record<string, unknown>;
  }, [
    claimFormEdits,
    contract,
    customerName,
    customerPhone,
    customerAddress1,
    customerAddress2,
    customerCity,
    customerState,
    customerPostalCode,
    customerCountryCode,
    repairs,
    laborRate,
    servicerName,
    servicerPhone,
    servicerContactName,
    servicerMobile,
    wantText,
    contactEmail,
    paymentEmail,
    servicerAddress1,
    servicerAddress2,
    servicerCity,
    servicerState,
    servicerPostalCode,
    servicerCountryCode,
    mileageIn,
    invoiceNumber,
    repairOrderDate,
    arrivalMethod,
    atServiceCenter,
    reimbursement,
    vehicleNotes,
    additionalSources,
    resolvedFileSources,
    productType,
  ]);

  /** Builds `form_state` for servicer_info from estimateparser + current UI (never uses cached API ref). */
  const buildServicerInfoFormState = useCallback((): Record<string, unknown> => {
    if (!contract) return {};

    const parserResults = readParserResultsFromDocument(parsedClaimDocument);
    const editsForPayload = parserResults
      ? mergeClaimFormEditsPreferUi(
          buildClaimFormEditsFromParserResults(parserResults, repairs),
          claimFormEdits
        )
      : claimFormEdits;

    const base = buildUnifiedClaimPayload({
      contract,
      customerName,
      customerPhone,
      customerAddress: {
        address1: customerAddress1,
        address2: customerAddress2,
        city: customerCity,
        state: customerState,
        postalCode: customerPostalCode,
        countryCode: customerCountryCode,
      },
      repairs,
      laborRate,
      servicerName,
      servicerPhone,
      servicerContactName,
      servicerMobile,
      wantText,
      contactEmail,
      paymentEmail,
      servicerAddress: {
        address1: servicerAddress1,
        address2: servicerAddress2,
        city: servicerCity,
        state: servicerState,
        postalCode: servicerPostalCode,
        countryCode: servicerCountryCode,
      },
      servicerDetailsNull: false,
      servicerDealerNo: dealerNumber,
      mileageIn,
      invoiceNumber,
      repairOrderDate,
      arrivalMethod,
      atServiceCenter,
      reimbursement,
      vehicleNotes,
      additionalSources,
      sources: resolvedFileSources,
      productType,
    }) as Record<string, unknown>;

    return mergeClaimObjectWithFormEdits(base, editsForPayload);
  }, [
    claimFormEdits,
    contract,
    parsedClaimDocument,
    customerName,
    customerPhone,
    customerAddress1,
    customerAddress2,
    customerCity,
    customerState,
    customerPostalCode,
    customerCountryCode,
    repairs,
    laborRate,
    servicerName,
    servicerPhone,
    servicerContactName,
    servicerMobile,
    wantText,
    contactEmail,
    paymentEmail,
    servicerAddress1,
    servicerAddress2,
    servicerCity,
    servicerState,
    servicerPostalCode,
    servicerCountryCode,
    dealerNumber,
    mileageIn,
    invoiceNumber,
    repairOrderDate,
    arrivalMethod,
    atServiceCenter,
    reimbursement,
    vehicleNotes,
    additionalSources,
    resolvedFileSources,
    productType,
  ]);

  const lastScorePayloadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onClaimScoreEnrichedForm) return;
    const co = claimGenerationReview?.claimObject;
    if (!co || !claimObjectHasPartAndLaborLines(co) || !contract) {
      lastScorePayloadKeyRef.current = null;
      onClaimScoreEnrichedForm(null);
      return;
    }
    const scorePayload = mergeClaimObjectWithFormEdits(co, claimFormEdits);
    const payloadKey = stableStringifyForScoreKey(scorePayload);
    if (payloadKey === lastScorePayloadKeyRef.current) return;
    lastScorePayloadKeyRef.current = payloadKey;
    onClaimScoreEnrichedForm(scorePayload);
  }, [
    claimGenerationReview?.claimObject,
    onClaimScoreEnrichedForm,
    contract,
    claimFormEdits,
  ]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomAfterAddRef = useRef(false);
  const lastServicerFetchKeyRef = useRef<string | null>(null);
  const servicerFetchAbortRef = useRef<AbortController | null>(null);
  const servicerInfoFormStateRef = useRef<Record<string, unknown> | null>(
    restoredClaimGeneration?.claimObject ?? null
  );
  const isFetchingServicerInfoRef = useRef(false);
  const fetchServicerInfoRef = useRef<(fetchKey: string) => Promise<void>>(async () => {});
  const servicerClassificationRef = useRef(servicerClassification);
  servicerClassificationRef.current = servicerClassification;

  const currentPrepareRequestKey = useMemo(
    () => stableStringifyForScoreKey(getClaimRequests(buildApiFormState())),
    [buildApiFormState]
  );

 

  const addRepair = useCallback(() => {
    shouldScrollToBottomAfterAddRef.current = true;
    onRepairsChange((prev) => {
      const maxId = prev.reduce((m, r) => Math.max(m, r.id), 0);
      return [...prev, makeInitialClaimRepair(maxId + 1)];
    });
  }, [onRepairsChange]);

  useEffect(() => {
    if (!shouldScrollToBottomAfterAddRef.current) return;
    shouldScrollToBottomAfterAddRef.current = false;

    const anchor = bottomRef.current;
    if (!anchor) return;

    let parent: HTMLElement | null = anchor.parentElement;
    while (parent) {
      const { overflowY } = getComputedStyle(parent);
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        parent.scrollHeight > parent.clientHeight + 1
      ) {
        parent.scrollTo({ top: parent.scrollHeight, behavior: 'smooth' });
        return;
      }
      parent = parent.parentElement;
    }
    anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [repairs.length]);

  const removeRepair = useCallback(
    (idx: number) => {
      onRepairsChange((prev) => prev.filter((_, i) => i !== idx || prev.length <= 1));
    },
    [onRepairsChange]
  );

  const updateRepair = useCallback(
    (idx: number, field: keyof ClaimRepairEntry, value: string) => {
      if (field === 'id' || field === 'labor_lines' || field === 'part_lines') return;
      onRepairsChange((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    },
    [onRepairsChange]
  );

  const updateTireTread = useCallback(
    (idx: number, value: string) => {
      updateRepair(idx, 'tiretread', clampTireTreadInput(value));
    },
    [updateRepair]
  );

  const isTireProduct = isTireProductType(productType);

  const updateLaborField = useCallback(
    (repairIdx: number, lineIdx: number, field: keyof ClaimLaborLineRow, value: string) => {
      if (field === 'id') return;
      onRepairsChange((prev) =>
        prev.map((r, i) => {
          if (i !== repairIdx) return r;
          const lines = r.labor_lines.map((row, j) => {
            if (j !== lineIdx) return row;

            const fixedAmount = amountOnlyLaborLinesRef.current.get(row.id);
            const focus = laborDerivationFocusRef.current;
            const hoursDerivationActive = Boolean(
              fixedAmount &&
                focus &&
                typeof focus === 'object' &&
                focus.repairIdx === repairIdx &&
                focus.lineIdx === lineIdx
            );

            const updated = handleLaborFieldChange(row, field, value, {
              laborRate,
              isAmountOnlyPending: amountOnlyLaborLinesRef.current.has(row.id),
              hoursDerivationActive,
              fixedAmount,
              onLaborRateChange,
            });

            if (field === 'labor_amount' && amountOnlyLaborLinesRef.current.has(row.id)) {
              amountOnlyLaborLinesRef.current.set(row.id, updated.labor_amount.trim());
            }

            return updated;
          });
          return { ...r, labor_lines: lines };
        })
      );
    },
    [onRepairsChange, laborRate, onLaborRateChange]
  );

  const updatePartField = useCallback(
    (repairIdx: number, lineIdx: number, field: keyof ClaimPartLineRow, value: string) => {
      if (field === 'id') return;
      onRepairsChange((prev) =>
        prev.map((r, i) => {
          if (i !== repairIdx) return r;
          const lines = r.part_lines.map((row, j) =>
            j === lineIdx ? handleLaborLineFieldChange(row, field, value, false) : row
          );
          return { ...r, part_lines: lines };
        })
      );
    },
    [onRepairsChange]
  );

  const addLaborLine = useCallback(
    (repairIdx: number) => {
      onRepairsChange((prev) =>
        prev.map((r, i) =>
          i === repairIdx ? { ...r, labor_lines: [...r.labor_lines, makeEmptyLaborLineRow()] } : r
        )
      );
    },
    [onRepairsChange]
  );

  const addPartLine = useCallback(
    (repairIdx: number) => {
      onRepairsChange((prev) =>
        prev.map((r, i) => (i === repairIdx ? { ...r, part_lines: [...r.part_lines, makeEmptyPartLineRow()] } : r))
      );
    },
    [onRepairsChange]
  );

  const removeLaborLine = useCallback(
    (repairIdx: number, lineIdx: number) => {
      onRepairsChange((prev) =>
        prev.map((r, i) => {
          if (i !== repairIdx) return r;
          if (!repairAllowsRemovingLaborLine(r, lineIdx)) return r;
          return {
            ...r,
            labor_lines: r.labor_lines.filter((_, j) => j !== lineIdx),
          };
        })
      );
    },
    [onRepairsChange]
  );

  const removePartLine = useCallback(
    (repairIdx: number, lineIdx: number) => {
      onRepairsChange((prev) =>
        prev.map((r, i) => {
          if (i !== repairIdx) return r;
          if (!repairAllowsRemovingPartLine(r, lineIdx)) return r;
          return {
            ...r,
            part_lines: r.part_lines.filter((_, j) => j !== lineIdx),
          };
        })
      );
    },
    [onRepairsChange]
  );

  /** Back-calculate hours (amount ÷ rate) or forward-calculate amount (hours × rate) when labor rate changes. */
  useEffect(() => {
    const rate = laborRate.trim();
    if (!rate || parseNumericField(rate) <= 0) return;

    onRepairsChange((prev) => {
      let anyChanged = false;
      const next = prev.map((repair) => {
        const labor_lines = recalcLaborLinesForRateChange(repair.labor_lines, laborRate);
        const changed = labor_lines.some(
          (line, i) =>
            line.labor_hours !== repair.labor_lines[i]?.labor_hours ||
            line.labor_amount !== repair.labor_lines[i]?.labor_amount
        );
        if (!changed) return repair;
        anyChanged = true;
        return { ...repair, labor_lines };
      });
      return anyChanged ? next : prev;
    });
  }, [laborRate, onRepairsChange]);

  /** Keep claim object in sync when form fields are edited after 3csmapper. */
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gate on claimGenerationReview without re-running when it is first set
  useEffect(() => {
    if (!claimGenerationReview) return;
    setClaimGenerationReview((prev) => {
      if (!prev) return prev;
      const nextObject = mergeClaimObjectWithFormEdits(prev.claimObject, claimFormEdits);
      try {
        if (JSON.stringify(nextObject) === JSON.stringify(prev.claimObject)) return prev;
      } catch {
        /* fall through to update */
      }
      return { ...prev, claimObject: nextObject };
    });
  }, [claimFormEdits]);

  const applyServicerInfoResponse = useCallback(
    (data: Record<string, unknown>, servicerClassificationFromOutputs?: string | null): void => {
      const rawFormState = resolveServicerInfoFormState(data);
      servicerInfoFormStateRef.current = mergeClaimObjectWithFormEdits(
        structuredClone(rawFormState),
        claimFormEdits
      );

      const servicerData = mergeServicerApiData(
        extractServicerFromClaimBody({ data: [rawFormState] }),
        servicerClassificationFromOutputs
      );
      if (servicerData) onServicerFromApiChange?.(servicerData);
    },
    [onServicerFromApiChange, claimFormEdits]
  );

  const fetchServicerInfo = useCallback(
    async (fetchKey: string): Promise<void> => {
      if (!session || !token?.trim() || !contract || !servicerSourceId || !dealerSourceId) return;
      if (isFetchingServicerInfoRef.current) return;
      if (lastServicerFetchKeyRef.current === fetchKey) return;

      const controller = new AbortController();
      servicerFetchAbortRef.current = controller;

      const formState = buildServicerInfoFormState();

      isFetchingServicerInfoRef.current = true;
      setIsFetchingServicerInfo(true);
      try {
        const { data, servicerClassification: servicerClassificationFromOutputs } =
          await runClaimIntakeOperation<Record<string, unknown>>({
          session,
          event: 'servicer_info',
          userInput: {
            form_state: formState,
            servicer_source_id: servicerSourceId,
            dealer_source_id: dealerSourceId,
            token,
            ...buildStreamUserInputExtras(paymentAuthorizationAcknowledged, servicerClassification),
          },
          token,
          baseUrl: agentsBaseUrl,
          ...(aiworkerUsageId ? { aiworkerUsageId } : {}),
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const rawFormState = resolveServicerInfoFormState(data);
        const dealerNoFromResponse =
          extractServicerFromClaimBody({ data: [rawFormState] })?.dealer_no?.trim() ?? '';
        lastServicerFetchKeyRef.current =
          dealerNoFromResponse && servicerPostalReady(servicerPostalCode)
            ? buildServicerFetchKey(servicerName, servicerPostalCode, '')
            : fetchKey;
        applyServicerInfoResponse(data, servicerClassificationFromOutputs);
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return;
        // Record the key on failure so the effect does not re-fire for the same name|postal|phone.
        lastServicerFetchKeyRef.current = fetchKey;
        // eslint-disable-next-line no-console -- servicer_info auto-fetch
        console.error('[RepairsDocsTab] servicer_info fetch failed:', err);
        CtyShowToast('error', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
      } finally {
        isFetchingServicerInfoRef.current = false;
        if (!controller.signal.aborted) {
          setIsFetchingServicerInfo(false);
        }
      }
    },
    [
      session,
      token,
      contract,
      servicerSourceId,
      dealerSourceId,
      buildServicerInfoFormState,
      agentsBaseUrl,
      aiworkerUsageId,
      applyServicerInfoResponse,
      paymentAuthorizationAcknowledged,
      servicerClassification,
      servicerName,
      servicerPostalCode,
    ]
  );

  fetchServicerInfoRef.current = fetchServicerInfo;

  useEffect(() => {
    if (!session || !token?.trim() || !contract || !servicerSourceId || !dealerSourceId) return undefined;
    if (claimGenerationReview) return undefined;
    if (isFetchingServicerInfoRef.current) return undefined;

    const name = servicerName.trim();
    const postal = servicerPostalCode.trim();
    const nameZipReady = Boolean(name) && servicerPostalReady(postal);
    const phoneReady = servicerPhoneReady(servicerPhone);
    // Fetch when name and zip are both ready, or when the phone number is exactly 10 digits.
    if (!nameZipReady && !phoneReady) return undefined;
    const parserResults = readParserResultsFromDocument(parsedClaimDocument);
    if (parsedClaimDocument && !parserResults && !hasEstimateParserPrefill(repairs)) return undefined;

    const dealerResolved = Boolean(dealerNumber.trim());
    const fetchKey = dealerResolved
      ? buildServicerFetchKey(name, postal, '')
      : buildServicerFetchKey(name, postal, servicerPhone);
    if (dealerResolved) {
      if (fetchKey === lastServicerFetchKeyRef.current) return undefined;
    } else if (
      !servicerLookupChanged(
        lastServicerFetchKeyRef.current,
        fetchKey,
        nameZipReady,
        phoneReady
      )
    ) {
      return undefined;
    }

    const pdfTrigger = Boolean(
      parsedClaimDocument && nameZipReady && !servicerClassificationRef.current.trim()
    );
    const delayMs = pdfTrigger ? 0 : 500;
    const timerId = globalThis.setTimeout(() => {
      if (isFetchingServicerInfoRef.current) return;
      void fetchServicerInfoRef.current(fetchKey);
    }, delayMs);

    return () => globalThis.clearTimeout(timerId);
  }, [
    session,
    token,
    contract,
    servicerSourceId,
    dealerSourceId,
    servicerName,
    servicerPostalCode,
    servicerPhone,
    dealerNumber,
    parsedClaimDocument,
    repairs,
    claimGenerationReview,
  ]);

  useEffect(
    () => () => {
      servicerFetchAbortRef.current?.abort();
    },
    []
  );

  const applyClaimGenerationResult = useCallback(
    (claimBody: Record<string, unknown>, docItem: Record<string, unknown> | null): void => {
      const firstRow = Array.isArray(claimBody.data)
        ? (claimBody.data[0] as Record<string, unknown> | undefined)
        : undefined;

      // The API returns the unified payload directly in data[0] (no nested claim_object key).
      const rawObject: Record<string, unknown> =
        firstRow != null && typeof firstRow === 'object' && !Array.isArray(firstRow)
          ? firstRow
          : {};

      const rowSourceId = firstRow?.source_id;
      const fallbackSourceId = docItem?.source_id;
      let sid = '';
      if (typeof rowSourceId === 'string' && rowSourceId) {
        sid = rowSourceId;
      } else if (typeof fallbackSourceId === 'string') {
        sid = fallbackSourceId;
      }

      const requests = getClaimRequests(rawObject);
      if (requests.length > 0) {
        onRepairsChange((previous) => {
          const entries = mergeMappedRepairsWithUserEdits(
            previous,
            mapClaimRequestsToRepairEntries(requests)
          ).map((repair) => {
            repair.labor_lines.forEach((line) => {
              if (isAmountOnlyLaborLine(line)) {
                amountOnlyLaborLinesRef.current.set(line.id, line.labor_amount.trim());
              }
            });
            return {
              ...repair,
              labor_lines: recalcLaborLinesForRateChange(repair.labor_lines, laborRate),
            };
          });
          return entries;
        });
      }
      setClaimGenerationReview({ sourceId: sid, claimObject: rawObject });
    },
    [onRepairsChange, laborRate]
  );

  /** The generated result is reusable while the payload's `requests` array is unchanged. */
  const hasGeneratedLineItems =
    claimGenerationReview !== null && preparedClaimRequestKey === currentPrepareRequestKey;

  const validationValues = useMemo<RepairsDocsTabFormValues>(
    () => ({
      arrivalMethod,
      atServiceCenter,
      reimbursement,
      paymentEmail,
      contractNumber,
      mileageIn,
      invoiceNumber,
      repairOrderDate,
      vehicleVin,
      customerName,
      customerPhone,
      customerAddress1,
      customerPostalCode,
      servicerName,
      laborRate,
      servicerPostalCode,
      servicerAddress1,
      servicerPhone,
      contactEmail,
      repairs,
      productType,
      vehicleNotes,
      hasGeneratedLineItems,
    }),
    [
      arrivalMethod,
      atServiceCenter,
      reimbursement,
      paymentEmail,
      contractNumber,
      mileageIn,
      invoiceNumber,
      repairOrderDate,
      vehicleVin,
      customerName,
      customerPhone,
      customerAddress1,
      customerPostalCode,
      servicerName,
      laborRate,
      servicerPostalCode,
      servicerAddress1,
      servicerPhone,
      contactEmail,
      repairs,
      productType,
      vehicleNotes,
      hasGeneratedLineItems,
    ]
  );

  const validationResult = useMemo(
    () => (showFieldErrors ? validateRepairsDocsTab(validationValues) : null),
    [showFieldErrors, validationValues]
  );
  const fieldErrors = validationResult?.fields ?? {};

  const repairOrderDatePickerValue = useMemo(() => {
    const ymd = repairOrderDateToComparableYmd(repairOrderDate);
    if (!ymd) return null;
    try {
      return parseDate(ymd);
    } catch {
      return null;
    }
  }, [repairOrderDate]);

  const repairOrderDateMax = today(getLocalTimeZone());

  const serviceInfoComplete = isRepairsDocsServiceInfoComplete(validationValues);
  const vehicleTripComplete = isVehicleTripIntakeComplete(arrivalMethod, atServiceCenter, reimbursement);
  const invoiceMetaComplete = isRepairsDocsInvoiceMetaComplete(validationValues);

  const repairsThreeCsComplete = repairs.every((r) => {
    if (!isRepair3CsComplete(r)) return false;
    if (isTireProduct && !isRepairTireTreadComplete(r)) return false;
    return true;
  });
  const draftLinesComplete = repairs.every(repairHasAtLeastOneLineItem);
  const step3PrimaryBaseReady =
    Boolean(token?.trim()) &&
    Boolean(session) &&
    Boolean(serviceInfoComplete) &&
    Boolean(vehicleTripComplete) &&
    Boolean(invoiceMetaComplete) &&
    repairsThreeCsComplete &&
    draftLinesComplete;

  const canGenerateLineItems =
    step3PrimaryBaseReady && !isGeneratingClaim && !isSubmitting && !isFetchingServicerInfo;

  const canProceedToSubmit =
    Boolean(token?.trim()) &&
    Boolean(session) &&
    Boolean(serviceInfoComplete) &&
    Boolean(vehicleTripComplete) &&
    Boolean(invoiceMetaComplete) &&
    repairsThreeCsComplete &&
    draftLinesComplete &&
    hasGeneratedLineItems &&
    !isGeneratingClaim &&
    !isSubmitting;

  const handleGenerateLineItems = useCallback(async () => {
    if (!canGenerateLineItems) return;
    if (!session || !token?.trim() || !contract) return;

    const payload = buildApiFormState();

    setIsGeneratingClaim(true);
    try {
      const { data: claimBody, servicerClassification: servicerClassificationFromOutputs } =
        await runClaimIntakeOperation<Record<string, unknown>>({
        session,
        event: '3csmapper',
        userInput: {
          form_state: payload,
          token,
          ...buildStreamUserInputExtras(paymentAuthorizationAcknowledged, servicerClassification),
        },
        token: token ?? '',
        baseUrl: agentsBaseUrl,
        ...(aiworkerUsageId ? { aiworkerUsageId } : {}),
      });
      const servicerData = mergeServicerApiData(
        extractServicerFromClaimBody(claimBody),
        servicerClassificationFromOutputs
      );
      if (servicerData) onServicerFromApiChange?.(servicerData);
      const claimRow = Array.isArray(claimBody.data)
        ? (claimBody.data[0] as Record<string, unknown> | undefined)
        : undefined;
      if (claimRow != null && typeof claimRow === 'object' && !Array.isArray(claimRow)) {
        servicerInfoFormStateRef.current = claimRow;
      }
      applyClaimGenerationResult(claimBody, parsedClaimDocument);
      setShouldOpenReviewAfterPrepare(true);
      CtyShowToast('success', 'Claim data generated successfully.');
    } catch (claimErr: unknown) {
      setClaimGenerationReview(null);
      onPreparedClaimRequestKeyChange?.(null);
      // eslint-disable-next-line no-console -- prepare-claim failure detail for support
      console.error('[RepairsDocsTab] prepare claim failed:', claimErr);
      setSubmitErrorModal(
        buildGenericClaimIntakeErrorModal({ heading: 'Claim data could not be generated' })
      );
      CtyShowToast('warning', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
    } finally {
      setIsGeneratingClaim(false);
    }
  }, [
    canGenerateLineItems,
    token,
    session,
    agentsBaseUrl,
    buildApiFormState,
    parsedClaimDocument,
    aiworkerUsageId,
    applyClaimGenerationResult,
    onServicerFromApiChange,
    paymentAuthorizationAcknowledged,
    servicerClassification,
    onPreparedClaimRequestKeyChange,
  ]);

  const buildCurrentReviewData = useCallback((): ClaimGenerationReviewData | null => {
    if (!claimGenerationReview) return null;
    const sourceFromParsed = parsedClaimDocument?.source_id;
    const sourceId =
      claimGenerationReview.sourceId ||
      (typeof sourceFromParsed === 'string' ? sourceFromParsed : '');
    return {
      sourceId,
      claimObject: mergeClaimObjectWithFormEdits(claimGenerationReview.claimObject, claimFormEdits),
    };
  }, [claimGenerationReview, parsedClaimDocument, claimFormEdits]);

  useEffect(() => {
    if (!shouldOpenReviewAfterPrepare) return;
    const reviewData = buildCurrentReviewData();
    if (!reviewData) return;
    onPreparedClaimRequestKeyChange?.(currentPrepareRequestKey);
    setShouldOpenReviewAfterPrepare(false);
    onContinue(reviewData);
  }, [
    shouldOpenReviewAfterPrepare,
    buildCurrentReviewData,
    currentPrepareRequestKey,
    onPreparedClaimRequestKeyChange,
    onContinue,
  ]);

  const handleProceedToSubmit = useCallback(async () => {
    if (!canProceedToSubmit) return;
    if (!token?.trim() || !session) return;

    setIsSubmitting(true);
    try {
      const reviewData = buildCurrentReviewData();
      if (!reviewData) return;
      CtyShowToast('success', 'Claim data prepared. Review and confirm submission.');
      onContinue(reviewData);
    } catch (err: unknown) {
      void err;
      CtyShowToast('error', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canProceedToSubmit,
    token,
    session,
    buildCurrentReviewData,
    onContinue,
  ]);

  const handlePrimaryAction = useCallback(() => {
    if (!paymentAuthorizationAcknowledged) {
      setShowFieldErrors(true);
      document.getElementById('payment-authorization-disclaimer')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    const result = validateRepairsDocsTab(validationValues);
    if (!result.isValid) {
      setShowFieldErrors(true);
      const scrollId = firstRepairsDocsErrorScrollId(result.fields, result.repairs);
      if (scrollId) {
        document.getElementById(scrollId)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      return;
    }
    if (hasGeneratedLineItems) {
      void handleProceedToSubmit();
    } else {
      void handleGenerateLineItems();
    }
  }, [
    paymentAuthorizationAcknowledged,
    validationValues,
    hasGeneratedLineItems,
    handleProceedToSubmit,
    handleGenerateLineItems,
  ]);

  const partsLaborWarrantyComplete = isPartsLaborWarrantyComplete(vehicleNotes);

  const footerHintMessage = getProceedFooterHintMessage({
    serviceInfoComplete,
    vehicleTripComplete,
    paymentAuthorizationAcknowledged,
    invoiceMetaComplete,
    draftLinesComplete,
    repairsThreeCsComplete,
    hasGeneratedLineItems,
    showFieldErrors,
    partsLaborWarrantyComplete,
    token,
  });

  const showClaimGenOverlay = isGeneratingClaim;

  return (
    <>
      <ClaimSubmitErrorModal content={submitErrorModal} onClose={() => setSubmitErrorModal(null)} />
      <VehicleNotAtCenterModal
        isOpen={vehicleNotAtCenterModalOpen}
        onClose={() => setVehicleNotAtCenterModalOpen(false)}
        onStartNewClaim={() => globalThis.location.reload()}
      />
      {showClaimGenOverlay ? (
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/45 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="claim-gen-loading-title"
        >
          <div className="mx-4 w-full max-w-md rounded-xl border border-default-200 bg-content1 p-8 shadow-xl flex flex-col items-center gap-5 text-center">
            <Spinner size="lg" color="primary" />
            <div id="claim-gen-loading-title">
              <p className="text-base font-semibold text-foreground">{"Preparing Claim"}</p>
              <p className="text-sm text-default-500 mt-2">{"This step can take a while."}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="space-y-4 sm:space-y-6" aria-busy={showClaimGenOverlay}>
        <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground">{"Vehicle Details"}</h2>
            <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
              {"Required"}
            </Chip>
          </div>
          <p className="text-sm text-default-500 mb-5">{"Provide additional information about the vehicle and service center."}</p>
          <div className="space-y-5">
            <LabeledHorizontalRadios
              id={repairsDocsFieldScrollId('arrivalMethod')}
              label={"How Did The Vehicle Arrive At The Shop?"}
              value={arrivalMethod}
              onValueChange={onArrivalMethodChange}
              error={fieldErrors.arrivalMethod}
              options={[
                { value: 'driven', label: "Driven In" },
                { value: 'towed', label: "Towed" },
              ]}
            />
            <LabeledHorizontalRadios
              id={repairsDocsFieldScrollId('atServiceCenter')}
              label={"Is The Vehicle At The Service Center?"}
              value={atServiceCenter}
              onValueChange={handleAtServiceCenterChange}
              error={fieldErrors.atServiceCenter}
              options={[
                { value: 'yes', label: "Yes" },
                { value: 'no', label: "No" },
              ]}
            />
            <div
              className="mb-0 flex gap-2 rounded-lg border border-sky-200 dark:border-sky-500/40 bg-sky-50 dark:bg-sky-500/10 px-3 py-2.5 text-sm text-sky-800 dark:text-sky-300"
              role="note"
            >
              <Icon icon="mdi:information-outline" className="shrink-0 w-5 h-5" aria-hidden />
              <p>{VEHICLE_AT_CENTER_INFO_DISCLAIMER}</p>
            </div>
          </div>
        </div>

        {atServiceCenter === 'no' ? (
          <div className="flex flex-wrap items-center justify-between gap-3" ref={bottomRef}>
            <CtyButton
              variant="bordered"
              onPress={onBack}
              startContent={<Icon icon="mdi:arrow-left" width={18} />}
              className="font-semibold"
            >
              Back
            </CtyButton>
          </div>
        ) : (
          <>
        <div id="vehicle-notes-section">
          <VehicleNotesSection
            vehicleNotes={vehicleNotes}
            onVehicleNotesChange={onVehicleNotesChange}
            hideFluidAndCodes={isTireProduct}
            warrantyMonthsError={fieldErrors.warrantyMonths}
            warrantyMilesError={fieldErrors.warrantyMiles}
          />
        </div>

        <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground">Payment Method</h2>
            <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
              Required
            </Chip>
          </div>
          <p className="text-sm text-default-500 mb-5">{"Select payment preferences for this claim."}</p>
          <LabeledHorizontalRadios
            id={repairsDocsFieldScrollId('reimbursement')}
            label={"How would you like to be paid?"}
            value={reimbursement}
            onValueChange={(value) => {
              onReimbursementChange(value);
              if (value !== 'CONNEXPAY') onPaymentEmailChange('');
            }}
            error={fieldErrors.reimbursement}
            options={[
              { value: 'Check', label: "Check" },
              { value: 'CONNEXPAY', label: "Credit Card" },
            ]}
          />
          {reimbursement === 'CONNEXPAY' ? (
            <Input
              id={repairsDocsFieldScrollId('paymentEmail')}
              className="mt-4 max-w-lg"
              label={"Payment Email"}
              type="email"
              isRequired
              value={paymentEmail}
              onValueChange={onPaymentEmailChange}
              isInvalid={Boolean(fieldErrors.paymentEmail)}
              errorMessage={fieldErrors.paymentEmail}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
          ) : null}
          <div
            id="payment-authorization-disclaimer"
            className="mt-5 rounded-lg border border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-3 py-3"
          >
            <Checkbox
              isSelected={paymentAuthorizationAcknowledged}
              onValueChange={setPaymentAuthorizationAcknowledged}
              isInvalid={showFieldErrors && !paymentAuthorizationAcknowledged}
              classNames={{
                label: 'text-sm text-amber-900',
              }}
            >
              Any repairs made without prior authorization by us are subject to denial.            </Checkbox>
            {showFieldErrors && !paymentAuthorizationAcknowledged ? (
              <p className="text-tiny text-danger mt-2 pl-7">
                Please acknowledge this disclaimer before continuing.
              </p>
            ) : null}
          </div>
        </div>

        <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground mb-2">{"Invoice Details"}</h2>
            <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
              Required
            </Chip>
          </div>
          {showFieldErrors && validationResult?.vehicleVinMissing ? (
            <p className="text-tiny text-danger mb-4">
              {"Vehicle VIN is required. Complete contract lookup on the first step so the VIN is available."}
            </p>
          ) : null}
          {showFieldErrors && fieldErrors.contractNumber ? (
            <p id={repairsDocsFieldScrollId('contractNumber')} className="text-tiny text-danger mb-4">
              {"Contract number is required. Complete contract lookup on the first step."}
            </p>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              id={repairsDocsFieldScrollId('mileageIn')}
              label={"Mileage In"}
              placeholder="0"
              value={mileageIn}
              onValueChange={(v) => onMileageInChange(v.replace(/[^0-9]/g, ''))}
              isInvalid={Boolean(fieldErrors.mileageIn)}
              errorMessage={fieldErrors.mileageIn}
              inputMode="numeric"
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('invoiceNumber')}
              label={"Invoice Number"}
              value={invoiceNumber}
              onValueChange={onInvoiceNumberChange}
              isInvalid={Boolean(fieldErrors.invoiceNumber)}
              errorMessage={fieldErrors.invoiceNumber}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <I18nProvider locale="en-US">
              <div id={repairsDocsFieldScrollId('repairOrderDate')}>
                <DatePicker
                  label={"Repair Order Date"}
                  variant="bordered"
                  value={repairOrderDatePickerValue}
                  onChange={(date) => {
                    onRepairOrderDateChange(
                      date ? normalizeRepairOrderDateToMdy(date.toString()) : ''
                    );
                  }}
                  maxValue={repairOrderDateMax}
                  isInvalid={Boolean(fieldErrors.repairOrderDate)}
                  errorMessage={fieldErrors.repairOrderDate}
                  classNames={{ inputWrapper: 'border-default-300' }}
                />
              </div>
            </I18nProvider>
          </div>
        </div>

        <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground">Customer Information</h2>
            <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
              Required
            </Chip>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Input
              id={repairsDocsFieldScrollId('customerName')}
              label={"Customer Name"}
              value={customerName}
              onValueChange={onCustomerNameChange}
              isInvalid={Boolean(fieldErrors.customerName)}
              errorMessage={fieldErrors.customerName}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('customerPhone')}
              label={"Customer Phone"}
              value={customerPhone}
              onValueChange={onCustomerPhoneChange}
              isInvalid={Boolean(fieldErrors.customerPhone)}
              errorMessage={fieldErrors.customerPhone}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('customerAddress1')}
              label={"Address Line 1"}
              value={customerAddress1}
              onValueChange={onCustomerAddress1Change}
              isInvalid={Boolean(fieldErrors.customerAddress1)}
              errorMessage={fieldErrors.customerAddress1}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
              className="sm:col-span-2"
            />
            <Input
              label={"Address Line 2"}
              value={customerAddress2}
              onValueChange={onCustomerAddress2Change}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
              className="sm:col-span-2"
            />
            <Input
              label={"City"}
              value={customerCity}
              onValueChange={onCustomerCityChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              label={"State"}
              value={customerState}
              onValueChange={onCustomerStateChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('customerPostalCode')}
              label={"Postal Code"}
              value={customerPostalCode}
              onValueChange={(v) => onCustomerPostalCodeChange(sanitizePostalCodeInput(v))}
              isInvalid={Boolean(fieldErrors.customerPostalCode)}
              errorMessage={fieldErrors.customerPostalCode}
              inputMode="numeric"
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            {/* <Input
              label={"Country Code"}
              value={customerCountryCode}
              onValueChange={onCustomerCountryCodeChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            /> */}
          </div>
        </div>

        <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
            <h2 className="text-base sm:text-lg font-semibold text-foreground">{"Service Center Information"}</h2>           
            <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
              {"Required"}
            </Chip>
          </div>
          {isFetchingServicerInfo ? (
            <p className="text-sm text-primary mb-3">{"Fetching servicer information…"}</p>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              id={repairsDocsFieldScrollId('servicerName')}
              label={"Servicer Name"}
              placeholder="Enter Service Center Name"
              value={servicerName}
              onValueChange={onServicerNameChange}
              isInvalid={Boolean(fieldErrors.servicerName)}
              errorMessage={fieldErrors.servicerName}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('laborRate')}
              label={"Labor Rate ($/Hour)"}
              value={isLaborRateFocused ? laborRate : formatAmountInputDisplay(laborRate)}
              isRequired
              onValueChange={(v) => handleLaborRateChange(sanitizeCurrencyAmountInput(v))}
              onFocus={() => {
                setIsLaborRateFocused(true);
                handleLaborRateFocus();
              }}
              onBlur={() => {
                setIsLaborRateFocused(false);
                handleLaborRateBlur();
              }}
              isInvalid={Boolean(fieldErrors.laborRate)}
              errorMessage={fieldErrors.laborRate}
              inputMode="decimal"
              startContent={<span className="text-default-400">$</span>}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('servicerPostalCode')}
              label={"Postal Code"}
              value={servicerPostalCode}
              onValueChange={(v) => onServicerPostalCodeChange(sanitizePostalCodeInput(v))}
              isInvalid={Boolean(fieldErrors.servicerPostalCode)}
              errorMessage={fieldErrors.servicerPostalCode}
              inputMode="numeric"
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            {servicerClassification.trim() ? (
              <div className="sm:col-span-2">
                <LabeledHorizontalRadios
                  label={"Servicer Classification"}
                  value={normalizeServicerClassification(servicerClassification)}
                  onValueChange={(v) => onServicerClassificationChange(normalizeServicerClassification(v))}
                  options={SERVICER_CLASSIFICATION_OPTIONS}
                />
              </div>
            ) : null}
            <Input
              id={repairsDocsFieldScrollId('contactEmail')}
              label={"Contact Email"}
              value={contactEmail}
              onValueChange={onContactEmailChange}
              isRequired
              isInvalid={Boolean(fieldErrors.contactEmail)}
              errorMessage={fieldErrors.contactEmail}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              label={"Contact Name"}
              value={servicerContactName}
              onValueChange={onServicerContactNameChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              id={repairsDocsFieldScrollId('servicerPhone')}
              label={"Servicer Phone Number"}
              isRequired
              value={servicerPhone}
              onValueChange={onServicerPhoneChange}
              isInvalid={Boolean(fieldErrors.servicerPhone)}
              errorMessage={fieldErrors.servicerPhone}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              label={"Mobile Number"}
              value={servicerMobile}
              onValueChange={onServicerMobileChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <div className="sm:col-span-2">
              <LabeledHorizontalRadios
                required={false}
                label={"Do you want us to text you?"}
                value={wantText}
                onValueChange={onWantTextChange}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
              />
            </div>
            <Input
              id={repairsDocsFieldScrollId('servicerAddress1')}
              label={"Address Line 1"}
              isRequired
              value={servicerAddress1}
              onValueChange={onServicerAddress1Change}
              isInvalid={Boolean(fieldErrors.servicerAddress1)}
              errorMessage={fieldErrors.servicerAddress1}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
              className="sm:col-span-2"
            />
            <Input
              label={"Address Line 2"}
              value={servicerAddress2}
              onValueChange={onServicerAddress2Change}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
              className="sm:col-span-2"
            />
            <Input
              label={"City"}
              value={servicerCity}
              onValueChange={onServicerCityChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            <Input
              label={"State"}
              value={servicerState}
              onValueChange={onServicerStateChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            />
            {/* <Input
              label={"Country Code"}
              value={servicerCountryCode}
              onValueChange={onServicerCountryCodeChange}
              variant="bordered"
              classNames={{ inputWrapper: 'border-default-300' }}
            /> */}
          </div>
        </div>

        {onAdditionalInfoFilesChange && onAdditionalSourcesChange ? (
          <AdditionalInformationUploadSection
            files={additionalInfoFiles}
            onFilesChange={onAdditionalInfoFilesChange}
            additionalSources={additionalSources ?? []}
            onAdditionalSourcesChange={onAdditionalSourcesChange}
            requirementChips={isTireProduct ? TWE_SUPPORTING_DOCUMENT_CHIPS : undefined}
          />
        ) : null}

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 -top-5 bg-content1 z-100 p-4 rounded-xl border border-default-200">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-foreground">{"Repairs"}</h2>
              <p className="text-sm text-default-500 mt-0.5">{"Each repair requires Complaint, Cause, and Correction (3Cs). Add or edit labor and part lines before generating claim data."}</p>
            </div>
            <CtyButton
              variant="bordered"
              color='primary'
              size="sm"
              onPress={addRepair}
              startContent={<Icon icon="mdi:plus" width={16} />}
              className="font-semibold shrink-0"
            >
              Add Repair
            </CtyButton>
          </div>

          <div className="space-y-4">
            {repairs.map((repair, idx) => {
              const complete = isRepair3CsComplete(repair);
              const lineCount = repair.labor_lines.length + repair.part_lines.length;
              const repairErrors = validationResult?.repairs[idx];
              return (
                <div
                  id={`repair-card-${idx}`}
                  key={repair.id}
                  className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6"
                >
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      {`Repair ${idx + 1}`}
                    </h3>
                    <Chip
                      size="sm"
                      color={complete ? 'success' : 'warning'}
                      variant="flat"
                      className="text-[10px] font-bold px-2"
                    >
                      {complete ? "3Cs Complete" : "3Cs Incomplete"}
                    </Chip>
                    <span className="text-xs text-default-400">
                      {`${lineCount} rows`}
                    </span>
                    {repairs.length > 1 ? (
                      <button
                        type="button"
                        className="ml-auto text-default-400 hover:text-red-500"
                        onClick={() => removeRepair(idx)}
                      >
                        <Icon icon="mdi:delete-outline" width={18} />
                      </button>
                    ) : null}
                  </div>

                  <div className="space-y-4">
                    <Textarea
                      label={
                        <span>
                          {"Complaint"} <span className="text-danger">*</span>
                        </span>
                      }
                      placeholder="Describe the customer's reported complaint in objective terms."
                      value={repair.complaint}
                      onValueChange={(v) => updateRepair(idx, 'complaint', v)}
                      isInvalid={Boolean(repairErrors?.complaint)}
                      errorMessage={repairErrors?.complaint}
                      variant="bordered"
                      minRows={2}
                      classNames={{ inputWrapper: 'border-default-300' }}
                    />
                    <Textarea
                      label={
                        <span>
                          {"Cause"} <span className="text-danger">*</span>
                        </span>
                      }
                      placeholder="Describe the root cause identified during diagnosis."
                      value={repair.cause}
                      onValueChange={(v) => updateRepair(idx, 'cause', v)}
                      isInvalid={Boolean(repairErrors?.cause)}
                      errorMessage={repairErrors?.cause}
                      variant="bordered"
                      minRows={2}
                      classNames={{ inputWrapper: 'border-default-300' }}
                    />
                    <Textarea
                      label={
                        <span>
                          {"Correction"} <span className="text-danger">*</span>
                        </span>
                      }
                      placeholder="Describe the corrective action performed to resolve the condition."
                      value={repair.correction}
                      onValueChange={(v) => updateRepair(idx, 'correction', v)}
                      isInvalid={Boolean(repairErrors?.correction)}
                      errorMessage={repairErrors?.correction}
                      variant="bordered"
                      minRows={2}
                      classNames={{ inputWrapper: 'border-default-300' }}
                    />

                    {isTireProduct ? (
                      <Input
                        label={
                          <span>
                            {"Tire Tread"} <span className="text-danger">*</span>
                          </span>
                        }
                        placeholder="0–32"
                        value={repair.tiretread}
                        onValueChange={(v) => updateTireTread(idx, v)}
                        isInvalid={Boolean(repairErrors?.tiretread)}
                        errorMessage={repairErrors?.tiretread}
                        inputMode="numeric"
                        min={0}
                        max={32}
                        variant="bordered"
                        classNames={{ inputWrapper: 'border-default-300' }}
                        className="max-w-xs"
                      />
                    ) : null}

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-foreground">{"Line Items"}</p>
                        <span className="text-xs text-default-400">
                          {hasGeneratedLineItems
                            ? 'Edit line items below — amounts are calculated from quantity × price (or hours × labor rate).'
                            : 'Add at least one labor or part line per repair before generating.'}
                        </span>
                      </div>
                      <PreGenLineTables
                        repairIdx={idx}
                        laborRate={laborRate}
                        laborLines={repair.labor_lines}
                        partLines={repair.part_lines}
                        onLaborField={updateLaborField}
                        onLaborHoursFocus={handleLaborHoursFocus}
                        onLaborHoursBlur={handleLaborHoursBlur}
                        isAmountOnlyPendingLine={isAmountOnlyPendingLine}
                        onPartField={updatePartField}
                        onAddLabor={addLaborLine}
                        onAddPart={addPartLine}
                        onRemoveLabor={removeLaborLine}
                        onRemovePart={removePartLine}
                        canRemoveLabor={(lineIdx) => repairAllowsRemovingLaborLine(repair, lineIdx)}
                        canRemovePart={(lineIdx) => repairAllowsRemovingPartLine(repair, lineIdx)}
                        showFieldErrors={showFieldErrors}
                        lineItemsSummary={repairErrors?.lineItemsSummary}
                        laborLineErrors={repairErrors?.laborLines}
                        partLineErrors={repairErrors?.partLines}
                        laborHeader={"Labor Lines"}
                        partHeader={"Part Lines"}
                        descLabel={"Description"}
                        hoursLabel={"Hours (hrs)"}
                        amountLabel={"Amount ($)"}
                        opCodeLabel={"Op Code"}
                        partNumLabel={"Part #"}
                        qtyLabel={"Qty"}
                        unitPriceLabel={"Unit Price ($)"}
                        addLabor={"Add Labor Line"}
                        addPart={"Add Part Line"}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3" ref={bottomRef}>
          <CtyButton
            variant="bordered"
            onPress={onBack}
            startContent={<Icon icon="mdi:arrow-left" width={18} />}
            className="font-semibold"
          >
            {"Back"}
          </CtyButton>
          <div className="flex flex-wrap items-center gap-3 justify-end max-w-xl">
            {footerHintMessage ? (
              <span className="text-sm text-orange-500 dark:text-orange-400">{footerHintMessage}</span>
            ) : null}
            {isFetchingServicerInfo && !hasGeneratedLineItems ? (
              <span className="text-sm text-primary">{"Fetching servicer information…"}</span>
            ) : null}
            <CtyButton
              color="primary"
              onPress={handlePrimaryAction}
              isLoading={isGeneratingClaim || isSubmitting}
              isDisabled={
                isGeneratingClaim ||
                isSubmitting ||
                (isFetchingServicerInfo && !hasGeneratedLineItems) ||
                !token?.trim() ||
                !session ||
                (!hasGeneratedLineItems && !contract)
              }
              endContent={
                isGeneratingClaim || isSubmitting ? undefined : <Icon icon="mdi:arrow-right" width={18} />
              }
              className="font-semibold"
            >
              {hasGeneratedLineItems ? "Proceed to Submit" : "Prepare Claim"}
            </CtyButton>
          </div>
        </div>
          </>
        )}
      </div>
    </>
  );
}
